import React, { useState, useEffect, useRef, Suspense, useCallback } from 'react';
import { Canvas, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls, Grid, Environment, useGLTF, Html, useProgress } from '@react-three/drei';
import * as THREE from 'three';
import FPSControls from './FPSControls';

// Bread spawn positions around the factory
const BREAD_SPAWN_POSITIONS: [number, number, number][] = [
  [-25, 3, -5],   // Near mixer
  [-25, 4, -20],  // Behind mixer
  [0, 3, -5],     // Near fermentor
  [0, 5, -20],    // Behind fermentor
  [25, 3, -5],    // Near oven
  [25, 4, -20],   // Behind oven
  [-30, 3, 15],   // Near control room
  [-20, 3, 20],   // Near control room entrance
  [-10, 6, 20],   // Near storage tank 1
  [0, 6, 20],     // Near storage tank 2
  [10, 6, 20],    // Near storage tank 3
  [20, 6, 20],    // Near storage tank 4
  [-35, 3, 0],    // Left wall
  [35, 3, 0],     // Right wall
  [0, 3, 0],      // Center
  [-15, 2, 10],   // Random floor
  [15, 2, 10],    // Random floor
  [-10, 2, -10],  // Random floor
  [10, 2, -10],   // Random floor
  [0, 8, 0],      // High center (challenge bread)
];

// All breads managed by a single component to avoid re-renders
function BreadGame({
  enabled,
  onScoreChange,
  onGameComplete
}: {
  enabled: boolean;
  onScoreChange: (score: number, total: number) => void;
  onGameComplete: () => void;
}) {
  const { camera } = useThree();
  const totalBreads = BREAD_SPAWN_POSITIONS.length;

  // Use refs to avoid re-renders on collection
  const collectedRef = useRef<boolean[]>(new Array(totalBreads).fill(false));
  const scoreRef = useRef(0);
  const meshRefs = useRef<(THREE.Group | null)[]>([]);
  const [, forceUpdate] = useState(0);

  // Reset when game is disabled/enabled
  useEffect(() => {
    if (enabled) {
      collectedRef.current = new Array(totalBreads).fill(false);
      scoreRef.current = 0;
      meshRefs.current = new Array(totalBreads).fill(null);
      onScoreChange(0, totalBreads);
      forceUpdate(n => n + 1);
    }
  }, [enabled, totalBreads, onScoreChange]);

  // Check collisions every frame without causing re-renders
  useFrame(() => {
    if (!enabled) return;

    for (let i = 0; i < totalBreads; i++) {
      if (collectedRef.current[i]) continue;

      const mesh = meshRefs.current[i];
      if (!mesh) continue;

      // Check distance using spawn position (mesh local position)
      const pos = BREAD_SPAWN_POSITIONS[i];
      const dx = pos[0] - camera.position.x;
      const dy = pos[1] - camera.position.y;
      const dz = pos[2] - camera.position.z;
      const distSq = dx * dx + dy * dy + dz * dz;

      if (distSq < 16) { // 4^2 = 16
        collectedRef.current[i] = true;
        scoreRef.current++;

        // Hide the mesh immediately
        mesh.visible = false;

        // Update score (batched, won't cause heavy re-render)
        onScoreChange(scoreRef.current, totalBreads);

        if (scoreRef.current === totalBreads) {
          onGameComplete();
        }
      }
    }
  });

  if (!enabled) return null;

  return (
    <group>
      {BREAD_SPAWN_POSITIONS.map((pos, i) => (
        <group
          key={i}
          position={pos}
          ref={(el) => { meshRefs.current[i] = el; }}
        >
          {/* Bread loaf shape */}
          <mesh castShadow rotation={[Math.PI / 2, 0, Math.random() * Math.PI]}>
            <capsuleGeometry args={[0.4, 0.8, 8, 16]} />
            <meshStandardMaterial
              color="#d4a574"
              roughness={0.8}
              metalness={0.1}
            />
          </mesh>
          {/* Golden glow */}
          <pointLight color="#ffd700" intensity={0.5} distance={5} />
          {/* Sparkle effect */}
          <mesh rotation={[0, performance.now() * 0.001 + i, 0]}>
            <sphereGeometry args={[0.8, 8, 8]} />
            <meshBasicMaterial color="#ffd700" transparent opacity={0.15} wireframe />
          </mesh>
        </group>
      ))}
    </group>
  );
}

// Game HUD overlay
function GameHUD({
  score,
  total,
  gameActive,
  gameComplete,
  onRestart
}: {
  score: number;
  total: number;
  gameActive: boolean;
  gameComplete: boolean;
  onRestart: () => void;
}) {
  if (!gameActive) return null;

  return (
    <>
      {/* Score counter */}
      <div
        style={{
          position: 'absolute',
          top: '1rem',
          right: '1rem',
          background: 'rgba(33, 40, 44, 0.95)',
          padding: '1rem 1.5rem',
          borderRadius: '8px',
          border: '2px solid #ffd700',
          color: '#ffd700',
          fontSize: '1.5rem',
          fontFamily: 'monospace',
          fontWeight: 'bold',
          zIndex: 20,
          textShadow: '0 0 10px rgba(255, 215, 0, 0.5)',
        }}
      >
        <div style={{ fontSize: '0.8rem', color: '#c5c5c5', marginBottom: '0.25rem' }}>
          BREAD COLLECTED
        </div>
        <div>
          🍞 {score} / {total}
        </div>
      </div>

      {/* Victory screen */}
      {gameComplete && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(33, 40, 44, 0.98)',
            padding: '3rem',
            borderRadius: '16px',
            border: '3px solid #ffd700',
            textAlign: 'center',
            zIndex: 30,
          }}
        >
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🏆</div>
          <div style={{
            fontSize: '2rem',
            color: '#ffd700',
            fontWeight: 'bold',
            marginBottom: '0.5rem',
            textShadow: '0 0 20px rgba(255, 215, 0, 0.5)',
          }}>
            VICTORY!
          </div>
          <div style={{ color: '#c5c5c5', marginBottom: '1.5rem' }}>
            You collected all {total} breads!
          </div>
          <button
            onClick={onRestart}
            style={{
              padding: '0.75rem 2rem',
              fontSize: '1rem',
              background: '#ffd700',
              border: 'none',
              borderRadius: '8px',
              color: '#1a1a1a',
              fontWeight: 'bold',
              cursor: 'pointer',
            }}
          >
            Play Again
          </button>
        </div>
      )}

      {/* Game start hint */}
      {score === 0 && !gameComplete && (
        <div
          style={{
            position: 'absolute',
            bottom: '5rem',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(33, 40, 44, 0.9)',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            border: '1px solid #ffd700',
            color: '#ffd700',
            fontSize: '0.9rem',
            fontFamily: 'monospace',
            zIndex: 20,
            animation: 'pulse 2s infinite',
          }}
        >
          Walk into the floating breads to collect them!
        </div>
      )}
    </>
  );
}

// Camera teleporter component - teleports camera based on mode
function CameraTeleporter({
  mode,
  fpsPosition,
  orbitPosition
}: {
  mode: 'orbit' | 'fps';
  fpsPosition: [number, number, number];
  orbitPosition: [number, number, number];
}) {
  const { camera } = useThree();
  const lastMode = useRef(mode);

  useEffect(() => {
    if (mode !== lastMode.current) {
      if (mode === 'fps') {
        // Teleport to center for FPS mode
        camera.position.set(...fpsPosition);
        camera.lookAt(fpsPosition[0], fpsPosition[1], fpsPosition[2] - 10);
      } else {
        // Reset to overview position for orbit mode
        camera.position.set(...orbitPosition);
        camera.lookAt(0, 0, 0);
      }
      lastMode.current = mode;
    }
  }, [mode, camera, fpsPosition, orbitPosition]);

  return null;
}

interface LoadProgress {
  loaded: number;
  total: number;
  percent: number;
}

// Placeholder factory model using basic shapes
function PlaceholderFactory() {
  return (
    <group>
      {/* Main building floor */}
      <mesh position={[0, 0.1, 0]} receiveShadow>
        <boxGeometry args={[80, 0.2, 60]} />
        <meshStandardMaterial color="#4a5568" />
      </mesh>

      {/* Walls */}
      <mesh position={[0, 5, -30]} castShadow>
        <boxGeometry args={[80, 10, 0.3]} />
        <meshStandardMaterial color="#718096" />
      </mesh>
      <mesh position={[0, 5, 30]} castShadow>
        <boxGeometry args={[80, 10, 0.3]} />
        <meshStandardMaterial color="#718096" />
      </mesh>
      <mesh position={[-40, 5, 0]} castShadow>
        <boxGeometry args={[0.3, 10, 60]} />
        <meshStandardMaterial color="#718096" />
      </mesh>
      <mesh position={[40, 5, 0]} castShadow>
        <boxGeometry args={[0.3, 10, 60]} />
        <meshStandardMaterial color="#718096" />
      </mesh>

      {/* Mixer station */}
      <group position={[-25, 0, -15]}>
        <mesh position={[0, 3, 0]} castShadow>
          <cylinderGeometry args={[4, 4, 6, 32]} />
          <meshStandardMaterial color="#53bde4" metalness={0.6} roughness={0.3} />
        </mesh>
        <mesh position={[0, 6.5, 0]} castShadow>
          <cylinderGeometry args={[1, 2, 1, 32]} />
          <meshStandardMaterial color="#2d3748" metalness={0.8} roughness={0.2} />
        </mesh>
        <Html position={[0, 8, 0]} center>
          <div style={{
            background: 'rgba(33, 40, 44, 0.9)',
            padding: '4px 8px',
            borderRadius: '4px',
            border: '1px solid #53bde4',
            color: '#53bde4',
            fontSize: '12px',
            fontFamily: 'monospace',
            whiteSpace: 'nowrap'
          }}>
            MIXER
          </div>
        </Html>
      </group>

      {/* Fermentor station */}
      <group position={[0, 0, -15]}>
        <mesh position={[0, 4, 0]} castShadow>
          <cylinderGeometry args={[5, 5, 8, 32]} />
          <meshStandardMaterial color="#48bb78" metalness={0.5} roughness={0.4} />
        </mesh>
        <mesh position={[0, 8.5, 0]} castShadow>
          <sphereGeometry args={[2, 32, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
          <meshStandardMaterial color="#48bb78" metalness={0.5} roughness={0.4} />
        </mesh>
        <Html position={[0, 11, 0]} center>
          <div style={{
            background: 'rgba(33, 40, 44, 0.9)',
            padding: '4px 8px',
            borderRadius: '4px',
            border: '1px solid #48bb78',
            color: '#48bb78',
            fontSize: '12px',
            fontFamily: 'monospace',
            whiteSpace: 'nowrap'
          }}>
            FERMENTOR
          </div>
        </Html>
      </group>

      {/* Oven station */}
      <group position={[25, 0, -15]}>
        <mesh position={[0, 3, 0]} castShadow>
          <boxGeometry args={[10, 6, 8]} />
          <meshStandardMaterial color="#ed8936" metalness={0.4} roughness={0.5} />
        </mesh>
        <mesh position={[0, 6.5, 0]} castShadow>
          <boxGeometry args={[8, 1, 6]} />
          <meshStandardMaterial color="#2d3748" metalness={0.7} roughness={0.3} />
        </mesh>
        <Html position={[0, 8, 0]} center>
          <div style={{
            background: 'rgba(33, 40, 44, 0.9)',
            padding: '4px 8px',
            borderRadius: '4px',
            border: '1px solid #ed8936',
            color: '#ed8936',
            fontSize: '12px',
            fontFamily: 'monospace',
            whiteSpace: 'nowrap'
          }}>
            OVEN
          </div>
        </Html>
      </group>

      {/* Conveyor belts */}
      <mesh position={[-12.5, 1, -15]} castShadow>
        <boxGeometry args={[15, 0.3, 2]} />
        <meshStandardMaterial color="#2d3748" />
      </mesh>
      <mesh position={[12.5, 1, -15]} castShadow>
        <boxGeometry args={[15, 0.3, 2]} />
        <meshStandardMaterial color="#2d3748" />
      </mesh>

      {/* Control room */}
      <group position={[-30, 0, 20]}>
        <mesh position={[0, 2.5, 0]} castShadow>
          <boxGeometry args={[12, 5, 10]} />
          <meshStandardMaterial color="#4a5568" />
        </mesh>
        {/* Windows */}
        <mesh position={[6.1, 3, 0]}>
          <boxGeometry args={[0.1, 2, 6]} />
          <meshStandardMaterial color="#63b3ed" transparent opacity={0.5} />
        </mesh>
        <Html position={[0, 6, 0]} center>
          <div style={{
            background: 'rgba(33, 40, 44, 0.9)',
            padding: '4px 8px',
            borderRadius: '4px',
            border: '1px solid #a0aec0',
            color: '#a0aec0',
            fontSize: '12px',
            fontFamily: 'monospace',
            whiteSpace: 'nowrap'
          }}>
            CONTROL ROOM
          </div>
        </Html>
      </group>

      {/* Storage tanks */}
      {[-10, 0, 10, 20].map((x, i) => (
        <group key={i} position={[x, 0, 20]}>
          <mesh position={[0, 5, 0]} castShadow>
            <cylinderGeometry args={[2, 2, 10, 16]} />
            <meshStandardMaterial color="#a0aec0" metalness={0.7} roughness={0.3} />
          </mesh>
        </group>
      ))}

      {/* Camera/sensor positions */}
      {[[-25, 8, -10], [0, 10, -10], [25, 8, -10], [-25, 6, 10]].map((pos, i) => (
        <mesh key={i} position={pos as [number, number, number]} castShadow>
          <boxGeometry args={[0.5, 0.5, 1]} />
          <meshStandardMaterial color="#e53e3e" emissive="#e53e3e" emissiveIntensity={0.5} />
        </mesh>
      ))}

      {/* Pillars */}
      {[
        [-35, 0, -25], [-35, 0, 0], [-35, 0, 25],
        [35, 0, -25], [35, 0, 0], [35, 0, 25],
        [0, 0, -25], [0, 0, 25]
      ].map((pos, i) => (
        <mesh key={i} position={[pos[0], 5, pos[2]]} castShadow>
          <cylinderGeometry args={[0.5, 0.5, 10, 8]} />
          <meshStandardMaterial color="#718096" />
        </mesh>
      ))}

      {/* Roof beams */}
      <mesh position={[0, 10, 0]}>
        <boxGeometry args={[80, 0.5, 0.5]} />
        <meshStandardMaterial color="#4a5568" />
      </mesh>
      {[-20, 0, 20].map((z, i) => (
        <mesh key={i} position={[0, 10, z]}>
          <boxGeometry args={[80, 0.5, 0.5]} />
          <meshStandardMaterial color="#4a5568" />
        </mesh>
      ))}
    </group>
  );
}

// Wall-mounted display panel with iframe showing the app
function WallDisplayPanel({
  position,
  rotation,
  isCarrying,
  onPlace,
  groupRef
}: {
  position: [number, number, number];
  rotation: [number, number, number];
  isCarrying: boolean;
  onPlace: (pos: [number, number, number], rotY: number) => void;
  groupRef?: React.RefObject<THREE.Group>;
}) {
  const { camera, scene } = useThree();
  const internalRef = useRef<THREE.Group>(null);
  const ref = groupRef || internalRef;
  const raycaster = useRef(new THREE.Raycaster());

  // When carrying, raycast to find walls and snap to them
  useFrame(() => {
    if (!isCarrying || !ref.current) return;

    // Get camera forward direction
    const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);

    // Setup raycaster from camera position in forward direction
    raycaster.current.set(camera.position, forward);

    // Find all meshes in the scene (excluding our panel group's children)
    const meshes: THREE.Object3D[] = [];
    scene.traverse((obj) => {
      if (obj instanceof THREE.Mesh && !ref.current?.getObjectById(obj.id)) {
        meshes.push(obj);
      }
    });

    // Raycast against scene meshes
    const intersects = raycaster.current.intersectObjects(meshes, false);

    if (intersects.length > 0 && intersects[0].face) {
      const hit = intersects[0];
      const normal = hit.face.normal.clone();

      // Transform normal to world space
      const normalMatrix = new THREE.Matrix3().getNormalMatrix(hit.object.matrixWorld);
      normal.applyMatrix3(normalMatrix).normalize();

      // Check if this is a roughly vertical surface (wall)
      const isWall = Math.abs(normal.y) < 0.5;

      if (isWall) {
        // Position panel slightly in front of wall (offset by panel depth)
        const wallPos = hit.point.clone().add(normal.multiplyScalar(0.5));
        ref.current.position.copy(wallPos);

        // Orient panel to face away from wall (same direction as normal)
        const targetLook = wallPos.clone().add(hit.face.normal.clone().applyMatrix3(normalMatrix).normalize());
        ref.current.lookAt(targetLook);
      } else {
        // Not a wall - just position in front of camera
        const newPos = camera.position.clone().add(forward.multiplyScalar(15));
        ref.current.position.copy(newPos);
        ref.current.lookAt(camera.position);
        ref.current.rotateY(Math.PI);
      }
    } else {
      // No intersection - position in front of camera
      const newPos = camera.position.clone().add(forward.multiplyScalar(15));
      ref.current.position.copy(newPos);
      ref.current.lookAt(camera.position);
      ref.current.rotateY(Math.PI);
    }
  });

  const handleClick = () => {
    if (isCarrying && ref.current) {
      const pos = ref.current.position;
      const rotY = ref.current.rotation.y;
      onPlace([pos.x, pos.y, pos.z], rotY);
    }
  };

  return (
    <group
      ref={ref}
      position={isCarrying ? [0, 0, 0] : position}
      rotation={isCarrying ? [0, 0, 0] : rotation}
      onClick={handleClick}
    >
      {/* Iframe with the app */}
      <Html
        transform
        distanceFactor={1}
        position={[0, 0, 0.1]}
        style={{
          width: '1200px',
          height: '900px',
          pointerEvents: isCarrying ? 'none' : 'auto',
        }}
      >
        <iframe
          src="http://localhost:3000"
          title="Kalis App"
          style={{
            width: '1200px',
            height: '900px',
            border: `2px solid ${isCarrying ? '#ffd700' : '#53bde4'}`,
            borderRadius: '4px',
            background: '#1a1d21',
            pointerEvents: isCarrying ? 'none' : 'auto',
            opacity: isCarrying ? 0.7 : 1,
          }}
        />
      </Html>

      {/* Carrying indicator */}
      {isCarrying && (
        <Html center position={[0, -6, 0]}>
          <div style={{
            background: 'rgba(255, 215, 0, 0.9)',
            color: '#1a1a1a',
            padding: '8px 16px',
            borderRadius: '4px',
            fontFamily: 'monospace',
            fontSize: '14px',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
          }}>
            Point at wall · Press E to place
          </div>
        </Html>
      )}
    </group>
  );
}

// GLB Model loader component
function GLBModel({ url }: { url: string }) {
  const { scene } = useGLTF(url);

  // Center and scale the model on mount
  useEffect(() => {
    if (!scene) return;

    const box = new THREE.Box3().setFromObject(scene);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());

    console.log('GLB Model loaded:', {
      url,
      center: center.toArray(),
      size: size.toArray(),
      min: box.min.toArray(),
      max: box.max.toArray(),
      childrenCount: scene.children.length
    });

    // Calculate scale to fit in ~100 unit space
    const maxDim = Math.max(size.x, size.y, size.z);
    const scale = maxDim > 0 ? 100 / maxDim : 1;

    // Apply transforms directly to scene
    scene.scale.setScalar(scale);
    // Center horizontally, place on floor (y=0)
    scene.position.set(
      -center.x * scale,
      -box.min.y * scale,
      -center.z * scale
    );

    // Ensure materials are visible and disable frustum culling
    scene.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
        // Disable frustum culling to prevent models from disappearing when moving camera
        child.frustumCulled = false;
        // If no material, add a default one
        if (!child.material) {
          child.material = new THREE.MeshStandardMaterial({ color: 0x888888 });
        }
      }
    });
  }, [scene, url]);

  return <primitive object={scene} />;
}

function ControlsHUD({ mode, onModeChange, gameActive, isCarryingPanel }: { mode: 'orbit' | 'fps'; onModeChange: (mode: 'orbit' | 'fps') => void; gameActive?: boolean; isCarryingPanel?: boolean }) {
  return (
    <div
      style={{
        position: 'absolute',
        bottom: '1rem',
        left: '1rem',
        background: 'rgba(33, 40, 44, 0.9)',
        padding: '1rem',
        borderRadius: '8px',
        border: '1px solid rgba(108, 117, 125, 0.3)',
        color: '#c5c5c5',
        fontSize: '0.85rem',
        maxWidth: '280px',
        zIndex: 10,
      }}
    >
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <button
          onClick={() => onModeChange('orbit')}
          style={{
            padding: '0.4rem 0.8rem',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            background: mode === 'orbit' ? '#53bde4' : 'rgba(108, 117, 125, 0.3)',
            color: mode === 'orbit' ? '#1a1a1a' : '#c5c5c5',
            fontWeight: mode === 'orbit' ? 600 : 400,
          }}
        >
          Orbit Mode
        </button>
        <button
          onClick={() => onModeChange('fps')}
          style={{
            padding: '0.4rem 0.8rem',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            background: mode === 'fps' ? '#53bde4' : 'rgba(108, 117, 125, 0.3)',
            color: mode === 'fps' ? '#1a1a1a' : '#c5c5c5',
            fontWeight: mode === 'fps' ? 600 : 400,
          }}
        >
          FPS Mode
        </button>
      </div>

      {mode === 'orbit' ? (
        <div style={{ lineHeight: 1.6 }}>
          <div><strong>Left Mouse</strong> - Rotate</div>
          <div><strong>Right Mouse</strong> - Pan</div>
          <div><strong>Scroll</strong> - Zoom</div>
        </div>
      ) : (
        <div style={{ lineHeight: 1.6 }}>
          <div><strong>Click</strong> - Lock cursor</div>
          <div><strong>WASD</strong> - Move</div>
          <div><strong>Mouse</strong> - Look around</div>
          <div><strong>Space</strong> - Move up</div>
          <div><strong>Ctrl</strong> - Move down</div>
          <div><strong>Shift</strong> - Sprint</div>
          <div><strong>Esc</strong> - Unlock cursor</div>
          <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid rgba(108, 117, 125, 0.3)' }}>
            <strong style={{ color: '#53bde4' }}>P</strong> - {isCarryingPanel ? 'Cancel' : 'Pick up'} Panel
            {isCarryingPanel && <span> | <strong style={{ color: '#53bde4' }}>E</strong> - Place</span>}
          </div>
          <div style={{ marginTop: '0.25rem' }}>
            <strong style={{ color: '#ffd700' }}>G</strong> - {gameActive ? 'End' : 'Start'} Bread Hunt 🍞
          </div>
        </div>
      )}
    </div>
  );
}

function InfoPanel() {
  return (
    <div
      style={{
        position: 'absolute',
        top: '1rem',
        left: '1rem',
        background: 'rgba(33, 40, 44, 0.9)',
        padding: '1rem',
        borderRadius: '8px',
        border: '1px solid rgba(83, 189, 228, 0.3)',
        color: '#c5c5c5',
        fontSize: '0.85rem',
        maxWidth: '300px',
        zIndex: 10,
      }}
    >
      <div style={{
        color: '#53bde4',
        fontWeight: 'bold',
        marginBottom: '0.5rem',
        fontSize: '1rem',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
        <span>🏭</span> Donje Svetice Facility
      </div>
      <div style={{ color: '#8b949e', fontSize: '0.8rem', lineHeight: 1.5 }}>
        3D representation of the factory floor.
        Use orbit mode to inspect or FPS mode to walk through.
      </div>
      <div style={{
        marginTop: '0.75rem',
        paddingTop: '0.75rem',
        borderTop: '1px solid rgba(108, 117, 125, 0.3)',
        color: '#6c757d',
        fontSize: '0.75rem'
      }}>
        To load your actual CAD model, convert the STEP file to GLB format and place it in <code>/public/models/factory.glb</code>
      </div>
    </div>
  );
}

// Loading overlay component
function LoadingOverlay() {
  const { active, progress, item } = useProgress();

  if (!active) return null;

  // Extract filename from the item path
  const fileName = item ? item.split('/').pop() : 'model';

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(26, 29, 33, 0.95)',
        zIndex: 100,
      }}
    >
      <div
        style={{
          background: 'rgba(33, 40, 44, 0.95)',
          padding: '2rem 3rem',
          borderRadius: '12px',
          border: '1px solid rgba(83, 189, 228, 0.3)',
          textAlign: 'center',
          minWidth: '300px',
        }}
      >
        {/* Factory icon with animation */}
        <div
          style={{
            fontSize: '3rem',
            marginBottom: '1rem',
            animation: 'pulse 2s ease-in-out infinite',
          }}
        >
          🏭
        </div>

        {/* Loading text */}
        <div
          style={{
            color: '#53bde4',
            fontSize: '1.1rem',
            fontWeight: 'bold',
            marginBottom: '0.5rem',
          }}
        >
          Loading 3D Model
        </div>

        {/* File name */}
        <div
          style={{
            color: '#8b949e',
            fontSize: '0.8rem',
            marginBottom: '1.5rem',
            fontFamily: 'monospace',
          }}
        >
          {fileName}
        </div>

        {/* Progress bar container */}
        <div
          style={{
            width: '100%',
            height: '8px',
            background: 'rgba(108, 117, 125, 0.3)',
            borderRadius: '4px',
            overflow: 'hidden',
            marginBottom: '0.75rem',
          }}
        >
          {/* Progress bar fill */}
          <div
            style={{
              width: `${progress}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #53bde4, #48bb78)',
              borderRadius: '4px',
              transition: 'width 0.3s ease',
            }}
          />
        </div>

        {/* Percentage */}
        <div
          style={{
            color: '#c5c5c5',
            fontSize: '0.9rem',
            fontFamily: 'monospace',
          }}
        >
          {progress.toFixed(0)}%
        </div>
      </div>

      {/* CSS animation for pulse */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
}

interface Factory3DViewerProps {
  modelUrl?: string;
}

export function Factory3DViewer({ modelUrl }: Factory3DViewerProps) {
  const [mode, setMode] = useState<'orbit' | 'fps'>('orbit');
  const [useGlbModel, setUseGlbModel] = useState(false);
  const [gameActive, setGameActive] = useState(false);
  const [gameScore, setGameScore] = useState(0);
  const [gameTotal, setGameTotal] = useState(BREAD_SPAWN_POSITIONS.length);
  const [gameComplete, setGameComplete] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Panel positioning state
  const [panelPosition, setPanelPosition] = useState<[number, number, number]>([-39.8, 5, 0]);
  const [panelRotationY, setPanelRotationY] = useState(Math.PI / 2);
  const [showPanelControls, setShowPanelControls] = useState(false);
  const [isCarryingPanel, setIsCarryingPanel] = useState(false);

  const handlePlacePanel = useCallback((pos: [number, number, number], rotY: number) => {
    setPanelPosition(pos);
    setPanelRotationY(rotY);
    setIsCarryingPanel(false);
  }, []);

  // Check if GLB model exists (check for .glb version of any model URL)
  useEffect(() => {
    if (modelUrl) {
      // Convert any extension to .glb and check if it exists
      const glbUrl = modelUrl.replace(/\.(step|stp|iges|igs|brep)$/i, '.glb');
      fetch(glbUrl, { method: 'HEAD' })
        .then(res => {
          if (res.ok) setUseGlbModel(true);
        })
        .catch(() => {});
    }
  }, [modelUrl]);

  // Ref to track panel position when carrying
  const panelGroupRef = useRef<THREE.Group>(null);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape to exit FPS mode
      if (e.code === 'Escape' && mode === 'fps') {
        setTimeout(() => {
          if (!document.pointerLockElement) {
            setMode('orbit');
            setGameActive(false);
            setGameComplete(false);
          }
        }, 100);
      }
      // G to toggle bread game (only in FPS mode)
      if (e.code === 'KeyG' && mode === 'fps') {
        setGameActive(prev => {
          if (!prev) {
            setGameScore(0);
            setGameComplete(false);
          }
          return !prev;
        });
      }
      // E to place panel when carrying (FPS mode)
      if (e.code === 'KeyE' && isCarryingPanel && panelGroupRef.current) {
        const pos = panelGroupRef.current.position;
        const rotY = panelGroupRef.current.rotation.y;
        setPanelPosition([pos.x, pos.y, pos.z]);
        setPanelRotationY(rotY);
        setIsCarryingPanel(false);
      }
      // P to toggle panel carry mode
      if (e.code === 'KeyP') {
        setIsCarryingPanel(prev => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mode, isCarryingPanel]);

  const handleModeChange = useCallback((newMode: 'orbit' | 'fps') => {
    setMode(newMode);
    if (newMode === 'orbit') {
      setGameActive(false);
      setGameComplete(false);
    }
  }, []);

  const handleScoreChange = useCallback((score: number, total: number) => {
    setGameScore(score);
    setGameTotal(total);
  }, []);

  const handleGameComplete = useCallback(() => {
    setGameComplete(true);
  }, []);

  const handleRestart = useCallback(() => {
    setGameComplete(false);
    setGameScore(0);
    setGameActive(false);
    // Small delay then restart
    setTimeout(() => setGameActive(true), 100);
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        minHeight: '600px',
        position: 'relative',
        background: '#1a1d21',
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      <Canvas
        camera={{
          position: [60, 40, 60],
          fov: 60,
          near: 0.1,
          far: 2000,
        }}
        shadows
        gl={{ antialias: true, alpha: false }}
        onCreated={({ gl }) => {
          gl.setClearColor('#1a1d21');
          gl.toneMapping = THREE.ACESFilmicToneMapping;
          gl.toneMappingExposure = 0.7;
        }}
      >
        <Suspense fallback={null}>
          {/* Lighting - reduced intensity to prevent washed-out colors */}
          <ambientLight intensity={0.2} />
          <directionalLight
            position={[50, 100, 50]}
            intensity={0.6}
            castShadow
            shadow-mapSize={[2048, 2048]}
          />
          <directionalLight position={[-50, 50, -50]} intensity={0.25} />
          <hemisphereLight args={['#87CEEB', '#444444', 0.3]} />

          {/* Environment - city preset for more neutral lighting */}
          <Environment preset="city" background={false} />

          {/* Dark floor plane for contrast */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.02, 0]} receiveShadow>
            <planeGeometry args={[200, 200]} />
            <meshStandardMaterial color="#2a2d32" roughness={0.9} />
          </mesh>

          {/* Grid floor */}
          <Grid
            position={[0, -0.01, 0]}
            args={[200, 200]}
            cellSize={5}
            cellThickness={0.5}
            cellColor="#3d4147"
            sectionSize={20}
            sectionThickness={1}
            sectionColor="#4a5058"
            fadeDistance={200}
            fadeStrength={1}
            followCamera={false}
          />

          {/* Factory Model - use GLB if available, otherwise placeholder */}
          {useGlbModel && modelUrl ? (
            <GLBModel url={modelUrl.replace(/\.(step|stp|iges|igs|brep)$/i, '.glb')} />
          ) : (
            <PlaceholderFactory />
          )}

{/* Wall-mounted 3D display panel */}
          <WallDisplayPanel
            position={panelPosition}
            rotation={[0, panelRotationY, 0]}
            isCarrying={isCarryingPanel}
            onPlace={handlePlacePanel}
            groupRef={panelGroupRef}
          />

          {/* Bread Collection Game */}
          <BreadGame
            enabled={gameActive && mode === 'fps'}
            onScoreChange={handleScoreChange}
            onGameComplete={handleGameComplete}
          />

          {/* Camera teleporter - moves camera based on mode */}
          <CameraTeleporter
            mode={mode}
            fpsPosition={[0, 5, 0]}
            orbitPosition={[60, 40, 60]}
          />

          {/* Controls based on mode */}
          {mode === 'orbit' ? (
            <OrbitControls
              makeDefault
              enableDamping
              dampingFactor={0.05}
              minDistance={5}
              maxDistance={500}
            />
          ) : (
            <FPSControls moveSpeed={150} lookSpeed={0.002} enabled={true} />
          )}
        </Suspense>
      </Canvas>

      <LoadingOverlay />
      <InfoPanel />
      <ControlsHUD mode={mode} onModeChange={handleModeChange} gameActive={gameActive} isCarryingPanel={isCarryingPanel} />
      <GameHUD
        score={gameScore}
        total={gameTotal}
        gameActive={gameActive}
        gameComplete={gameComplete}
        onRestart={handleRestart}
      />

      {/* Panel Position Controls */}
      <div
        style={{
          position: 'absolute',
          top: '1rem',
          right: '1rem',
          zIndex: 20,
        }}
      >
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => setIsCarryingPanel(!isCarryingPanel)}
            style={{
              padding: '0.5rem 1rem',
              background: isCarryingPanel ? '#ffd700' : 'rgba(33, 40, 44, 0.9)',
              border: `1px solid ${isCarryingPanel ? '#ffd700' : '#53bde4'}`,
              borderRadius: '4px',
              color: isCarryingPanel ? '#1a1a1a' : '#53bde4',
              cursor: 'pointer',
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              fontWeight: isCarryingPanel ? 'bold' : 'normal',
            }}
          >
            {isCarryingPanel ? 'Placing...' : 'Move Panel'}
          </button>
          <button
            onClick={() => setShowPanelControls(!showPanelControls)}
            style={{
              padding: '0.5rem 1rem',
              background: showPanelControls ? '#53bde4' : 'rgba(33, 40, 44, 0.9)',
              border: '1px solid #53bde4',
              borderRadius: '4px',
              color: showPanelControls ? '#1a1a1a' : '#53bde4',
              cursor: 'pointer',
              fontFamily: 'monospace',
              fontSize: '0.8rem',
            }}
          >
            Fine Tune
          </button>
        </div>

        {showPanelControls && (
          <div
            style={{
              marginTop: '0.5rem',
              background: 'rgba(33, 40, 44, 0.95)',
              padding: '1rem',
              borderRadius: '8px',
              border: '1px solid rgba(83, 189, 228, 0.3)',
              color: '#c5c5c5',
              fontSize: '0.8rem',
              fontFamily: 'monospace',
              minWidth: '200px',
            }}
          >
            <div style={{ marginBottom: '0.75rem', color: '#53bde4', fontWeight: 'bold' }}>
              Position
            </div>

            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>X:</span>
                <input
                  type="range"
                  min="-50"
                  max="50"
                  step="0.5"
                  value={panelPosition[0]}
                  onChange={(e) => setPanelPosition([parseFloat(e.target.value), panelPosition[1], panelPosition[2]])}
                  style={{ width: '100px' }}
                />
                <span style={{ width: '40px', textAlign: 'right' }}>{panelPosition[0].toFixed(1)}</span>
              </label>
            </div>

            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Y:</span>
                <input
                  type="range"
                  min="0"
                  max="20"
                  step="0.5"
                  value={panelPosition[1]}
                  onChange={(e) => setPanelPosition([panelPosition[0], parseFloat(e.target.value), panelPosition[2]])}
                  style={{ width: '100px' }}
                />
                <span style={{ width: '40px', textAlign: 'right' }}>{panelPosition[1].toFixed(1)}</span>
              </label>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Z:</span>
                <input
                  type="range"
                  min="-50"
                  max="50"
                  step="0.5"
                  value={panelPosition[2]}
                  onChange={(e) => setPanelPosition([panelPosition[0], panelPosition[1], parseFloat(e.target.value)])}
                  style={{ width: '100px' }}
                />
                <span style={{ width: '40px', textAlign: 'right' }}>{panelPosition[2].toFixed(1)}</span>
              </label>
            </div>

            <div style={{ marginBottom: '0.75rem', color: '#53bde4', fontWeight: 'bold', borderTop: '1px solid rgba(108, 117, 125, 0.3)', paddingTop: '0.75rem' }}>
              Rotation
            </div>

            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Y:</span>
                <input
                  type="range"
                  min="0"
                  max={Math.PI * 2}
                  step="0.1"
                  value={panelRotationY}
                  onChange={(e) => setPanelRotationY(parseFloat(e.target.value))}
                  style={{ width: '100px' }}
                />
                <span style={{ width: '40px', textAlign: 'right' }}>{(panelRotationY * 180 / Math.PI).toFixed(0)}°</span>
              </label>
            </div>

            <div style={{ marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid rgba(108, 117, 125, 0.3)', fontSize: '0.7rem', color: '#6c757d' }}>
              Position: [{panelPosition.map(p => p.toFixed(1)).join(', ')}]<br />
              Rotation Y: {panelRotationY.toFixed(2)} rad
            </div>
          </div>
        )}
      </div>

      {/* Crosshair for FPS mode */}
      {mode === 'fps' && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '20px',
            height: '20px',
            pointerEvents: 'none',
            zIndex: 10,
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '0',
              right: '0',
              height: '2px',
              background: 'rgba(255, 255, 255, 0.5)',
              transform: 'translateY(-50%)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: '50%',
              top: '0',
              bottom: '0',
              width: '2px',
              background: 'rgba(255, 255, 255, 0.5)',
              transform: 'translateX(-50%)',
            }}
          />
        </div>
      )}
    </div>
  );
}

export default Factory3DViewer;
